/* La barra laterale e il contenuto: il guscio di ogni pagina dopo l'accesso.
 *
 * **Dal 1 ottobre 2026 il menu sta in una barra laterale nel verde del
 * marchio**, come nel Portale: prima la testata era chiara come il contenuto e
 * "non si capiva dove sei" (Edoardo). Le voci di lavoro in alto, le
 * impostazioni staccate sotto, in fondo chi e' collegato. Le schede di una
 * sezione restano sotto il titolo della pagina, perche' portano i loro
 * conteggi (avvisi aperti, fatture da passare) e nella barra si perderebbero.
 *
 * **Sul telefono** la barra diventa una striscia verde in alto (marchio,
 * impostazioni, Esci) e le voci di lavoro scendono in una barra in basso,
 * sotto il pollice: niente menu nascosto dietro un bottone.
 *
 * **Il numero accanto a "Fatturare"** sono le fatture incassate e non ancora
 * emesse su Fatture Smart: l'unico lavoro del programma con una scadenza di
 * legge. Si rilegge a ogni cambio di pagina, cosi' dopo aver registrato un
 * numero scende senza ricaricare.
 */

import { NavLink, Outlet, useLocation, useNavigate } from "react-router-dom";
import { documenti, incassi, type Ruolo } from "../api";
import { useCarica } from "../carica";
import type { DaPassare, VersamentoDaFatturare } from "../dominio";
import { sezioniPer, type Sezione } from "../sezioni";
import { useSessione } from "../sessione";
import { ICONE } from "./icone";
import { MarchioCompatto, Sigla } from "./Marchio";
import { AvvisoBackup } from "./AvvisoBackup";
import { AvvisoDemo } from "./AvvisoDemo";
import { VISTE_FATTURARE } from "./Schede";

const RUOLO: Record<Ruolo, string> = {
  AMMINISTRATORE: "Amministratore",
  FATTURAZIONE: "Fatturazione",
  OPERATORE: "Operatore",
};

export interface ContestoGuscio {
  ricaricaCoda: () => Promise<void>;
}

// Il collegamento al Portale dei cedolini, solo se l'indirizzo e'
// configurato: le persone dello studio sono le stesse nei due programmi, le
// sessioni no, quindi dall'altra parte si entra di nuovo.
const URL_PORTALE = import.meta.env.VITE_URL_PORTALE as string | undefined;

function Voce({ sezione, conteggio, classe }: { sezione: Sezione; conteggio?: number; classe: string }) {
  return (
    <NavLink
      to={sezione.percorso}
      className={({ isActive }) => (isActive ? `${classe} corrente` : classe)}
    >
      {ICONE[sezione.percorso]}
      <span className="voce-testo">{sezione.nome}</span>
      {conteggio !== undefined && conteggio > 0 && (
        <span className="conteggio" title="fatture da emettere su Fatture Smart">
          {conteggio}
        </span>
      )}
    </NavLink>
  );
}

function VoceLaterale({ sezione, conteggio, pathname }: { sezione: Sezione; conteggio?: number; pathname: string }) {
  const attiva = pathname === sezione.percorso || pathname.startsWith(`${sezione.percorso}/`);
  const schede = sezione.schede ?? (sezione.percorso === "/fatturare" ? VISTE_FATTURARE : []);
  return (
    <div className="voce-gruppo">
      <Voce sezione={sezione} conteggio={conteggio} classe="voce" />
      {attiva && schede.length > 1 && (
        <nav className="sotto-voci" aria-label={`Pagine di ${sezione.nome}`}>
          {schede.map((scheda) => (
            <NavLink key={scheda.percorso} to={`${sezione.percorso}/${scheda.percorso}`} className={({ isActive }) => isActive ? "corrente" : undefined}>
              {scheda.nome}
            </NavLink>
          ))}
        </nav>
      )}
    </div>
  );
}

export function Guscio() {
  const { utente, esci } = useSessione();
  const naviga = useNavigate();
  const { pathname } = useLocation();
  const sezioni = utente ? sezioniPer(utente.ruolo) : [];
  const vedeFatturare = sezioni.some((s) => s.percorso === "/fatturare");
  const coda = useCarica<DaPassare[]>(
    () => (vedeFatturare ? documenti.daPassare() : Promise.resolve([])),
    [vedeFatturare, pathname],
  );
  // Gli acconti e i saldi da fatturare a mano hanno la stessa scadenza di 12 giorni.
  const aMano = useCarica<VersamentoDaFatturare[]>(
    () => (vedeFatturare ? incassi.daFatturare() : Promise.resolve([])),
    [vedeFatturare, pathname],
  );
  if (!utente) return null;

  async function esciEVaiVia() {
    await esci();
    naviga("/", { replace: true });
  }

  const lavoro = sezioni.filter((s) => !s.laterale);
  const laterali = sezioni.filter((s) => s.laterale);
  const conteggioDi = (s: Sezione) => (s.percorso === "/fatturare" ? (coda.dati?.length ?? 0) + (aMano.dati?.length ?? 0) : undefined);

  return (
    <div className="guscio">
      <a className="salta" href="#contenuto">Salta al contenuto</a>

      <aside className="barra-laterale">
        <div className="barra-marchio">
          <MarchioCompatto bicolore />
        </div>
        <p className="barra-programma">Fatturazione</p>
        <nav className="barra-menu" aria-label="Sezioni">
          {lavoro.map((sezione) => (
            <VoceLaterale key={sezione.percorso} sezione={sezione} conteggio={conteggioDi(sezione)} pathname={pathname} />
          ))}
          {laterali.length > 0 && (
            <div className="voce-gruppo laterale">
              {laterali.map((sezione) => (
                <VoceLaterale key={sezione.percorso} sezione={sezione} pathname={pathname} />
              ))}
            </div>
          )}
        </nav>
        <div className="barra-piede">
          <p className="barra-utente">
            <NavLink to="/password" className="barra-nome" title="Cambia la password">
              {utente.nome}
            </NavLink>
            <span className="barra-ruolo">{RUOLO[utente.ruolo]}</span>
          </p>
          {URL_PORTALE && (
            <a className="barra-altro" href={URL_PORTALE}>
              Vai al Portale
            </a>
          )}
          <button className="tasto barra-esci" type="button" onClick={esciEVaiVia}>
            Esci
          </button>
        </div>
      </aside>

      <header className="cima">
        <Sigla bicolore className="cima-sigla" />
        <span className="cima-programma">Fatturazione</span>
        {laterali.map((sezione) => (
          <NavLink
            key={sezione.percorso}
            to={sezione.percorso}
            className={({ isActive }) => (isActive ? "cima-link corrente" : "cima-link")}
          >
            {sezione.nome}
          </NavLink>
        ))}
        <button className="tasto cima-esci" type="button" onClick={esciEVaiVia}>
          Esci
        </button>
      </header>

      <main id="contenuto" className="contenuto" tabIndex={-1}>
        <AvvisoDemo />
        <AvvisoBackup ruolo={utente.ruolo} />
        <Outlet
          context={{
            ricaricaCoda: async () => {
              await Promise.all([coda.ricarica(), aMano.ricarica()]);
            },
          } satisfies ContestoGuscio}
        />
      </main>

      <nav className="barra-basso" aria-label="Sezioni in basso">
        {lavoro.map((sezione) => (
          <Voce key={sezione.percorso} sezione={sezione} conteggio={conteggioDi(sezione)} classe="voce-basso" />
        ))}
      </nav>
    </div>
  );
}
